import {
  useMutation,
  UseQueryOptions,
  useInfiniteQuery,
  UseInfiniteQueryResult,
} from '@tanstack/react-query';

import {
  PageWithLinks,
  PathParamsBase,
  UsePaginatedApiOptions,
} from 'api/httpEntities';
import { QueryParamsBase } from 'api/search';
import { sendContactForm } from './api';

import { pageParamFromString } from './utils';

export type QueryOptionsConfig<T> = Omit<
  UseQueryOptions<T, Error, T>,
  'queryKey' | 'queryFn'
>;

export const useSendContactForm = () =>
  useMutation({ mutationFn: sendContactForm });

/**
 * Represents the absence of path parameters.
 * Used as the default when no pathParams are required.
 * */
export type EmptyObject = Record<string, never>;

export const usePaginatedApi = <
  TPage extends PageWithLinks,
  TItem,
  TQueryParams extends QueryParamsBase,
  TPathParams extends PathParamsBase = EmptyObject,
>({
  fetchPage,
  queryKey,
  getItemsFromPage,
  initialPageParam,
  enabled = true,
  staleTime,
  queryParams,
  pathParams,
  placeholderData,
}: UsePaginatedApiOptions<
  TPage,
  TItem,
  TQueryParams,
  TPathParams
>): UseInfiniteQueryResult<
  {
    items: TItem[];
    totalCount?: number;
  },
  Error
> => {
  const specificQueryKey = [...queryKey, { queryParams }, { pathParams }];
  return useInfiniteQuery<
    TPage,
    Error,
    { items: TItem[]; totalCount?: number },
    typeof specificQueryKey,
    QueryParamsBase
  >({
    enabled,
    staleTime,
    queryKey: specificQueryKey,
    queryFn: ({ pageParam }) => {
      const mergedQueryParams = {
        ...(urlEncodeUserInput(queryParams) ?? {}),
        ...(pageParam ?? {}),
      } as TQueryParams;
      return fetchPage(mergedQueryParams, pathParams);
    },
    placeholderData: placeholderData,
    getNextPageParam: (lastPage) =>
      lastPage?.links?.next
        ? pageParamFromString('query', lastPage.links.next)
        : undefined,
    initialPageParam: initialPageParam
      ? pageParamFromString('responseSize', initialPageParam)
      : {},
    select: (response) => {
      const items = response.pages.flatMap((p) => getItemsFromPage(p) ?? []);
      const totalCount = response.pages.at(-1)?.totalCount ?? items.length;
      return { items, totalCount };
    },
  });
};

/**
 * Manually URL encoding a request is non-standard but exists here on purpose to conform to the requirements of the API.
 *
 * See the commit log for more details.
 */
function urlEncodeUserInput<T extends Record<string, unknown>>(
  params?: T,
): T | undefined {
  if (!params) {
    return undefined;
  }
  return Object.fromEntries(
    Object.entries(params).map(([key, value]) => [
      key,
      Array.isArray(value)
        ? value.map((v) => urlEncodeIfNeeded(v))
        : urlEncodeIfNeeded(value),
    ]),
  ) as T;
}

function urlEncodeIfNeeded(value: unknown) {
  return needsEncoding(value) ? encodeURIComponent(value) : value;
}

function needsEncoding(value: unknown): value is string | number | boolean {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}
